import { INVMGMT_SERVICE_URL } from "../utils/env.config";

export type WithAccessToken<T> = T & {
  accessToken?: string;
};

export const RequestFactory = {
  createGetRequest: (
    path: string,
    options: RequestInit = {},
    baseURL = INVMGMT_SERVICE_URL,
  ) => {
    const { headers, ...restOptions } = options;
    return new Request(`${baseURL}${path}`, {
      method: "GET",
      headers,
      ...restOptions,
    });
  },

  createPostRequest: <T>(
    path: string,
    body: T,
    options: RequestInit = {},
    baseURL = INVMGMT_SERVICE_URL,
  ) => {
    const { headers, ...restOptions } = options;

    return new Request(`${baseURL}${path}`, {
      method: "POST",
      body: JSON.stringify(body),
      headers,
      ...restOptions,
    });
  },

  createPostVoucherRequest: <T extends BodyInit | null | undefined>(
    path: string,
    body: T,
    options: RequestInit = {},
    baseURL = INVMGMT_SERVICE_URL,
  ) => {
    const { headers, ...restOptions } = options;

    return new Request(`${baseURL}${path}`, {
      method: "POST",
      body: body,
      headers,
      ...restOptions,
    });
  },

  createPatchRequest: <T>(
    path: string,
    body: T,
    options: RequestInit = {},
    baseURL = INVMGMT_SERVICE_URL,
  ) => {
    const { headers, ...restOptions } = options;

    return new Request(`${baseURL}${path}`, {
      method: "PATCH",
      body: JSON.stringify(body),
      headers,
      ...restOptions,
    });
  },

  createGetCsvRequest: (path: string, options: RequestInit = {}) => {
    const { headers, ...restOptions } = options;
    return new Request(`${path}`, {
      method: "GET",
      headers,
      ...restOptions,
    });
  },

  createPutCsvRequest: <T>(
    path: string,
    body: T,
    options: RequestInit = {},
    baseURL = INVMGMT_SERVICE_URL,
  ) => {
    const { headers, ...restOptions } = options;

    return new Request(`${baseURL}${path}`, {
      method: "PUT",
      body: JSON.stringify(body),
      headers,
      ...restOptions,
    });
  },

  createPutRequest: <T>(url: string, options: RequestInit = {}) => {
    return new Request(url, {
      method: "PUT",
      ...options,
    });
  },
};

export const fetchAndParse = async (request: Request) => {
  const response = await fetch(request);
  if (!response.ok && response.status === 404) {
    const text = await response.text();
    return {
      json: () => {
        return { httpStatus: 404, message: text };
      },
      blob: () => {
        return "Product does not exist!";
      },
      message: "Product does not exist!",
    };
  }

  if (!response.ok) {
    const responseData = await response.json();
    let errorMessage;

    if (
      (response.status === 400 || response.status === 500) &&
      responseData.message
    ) {
      errorMessage = responseData.message;
    } else {
      errorMessage = "Something went wrong. Please try again later!";
    }

    throw new Error(errorMessage);
  }

  return response;
};

export const fetchAndParseJson = async <T>(request: Request): Promise<T> => {
  const response = await fetchAndParse(request);
  return await response.json();
};
